// Librairies
import { Component, DoCheck, inject, Input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import {
  Duration, endOfDay, endOfHour, endOfMinute, endOfMonth, endOfSecond, endOfWeek, endOfYear, startOfDay, startOfHour,
  startOfMinute, startOfMonth, startOfSecond, startOfWeek, startOfYear, sub
} from 'date-fns';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

// Interfaces
type Unit = 'year' | 'month' | 'week' | 'day' | 'hour' | 'minute' | 'second';

export interface DateFilterRange {
  start: Date | null;
  end: Date | null;                       // null: up to now
  hours: { from: number | null; to: number | null } | null;   // minutes of the day, every day
}

export interface DaySegment {
  from: number;                           // % of the day
  to: number;
}

// Classes
// The API units as date-fns durations and bounds; a week starts on Monday, as on the API.
const DURATIONS: Record<Unit, keyof Duration> = { year: 'years', month: 'months', week: 'weeks', day: 'days', hour: 'hours', minute: 'minutes', second: 'seconds' };
const START_OF: Record<Unit, (date: Date) => Date> = {
  year: startOfYear, month: startOfMonth, week: date => startOfWeek(date, { weekStartsOn: 1 }), day: startOfDay,
  hour: startOfHour, minute: startOfMinute, second: startOfSecond
};
const END_OF: Record<Unit, (date: Date) => Date> = {
  year: endOfYear, month: endOfMonth, week: date => endOfWeek(date, { weekStartsOn: 1 }), day: endOfDay,
  hour: endOfHour, minute: endOfMinute, second: endOfSecond
};

const shift = (date: Date, amount: number, unit: Unit): Date => sub(date, { [DURATIONS[unit]]: amount });

const minutes = (hour: unknown, minute: unknown): number => (Number(hour) || 0) * 60 + (Number(minute) || 0);

// A period filter: from the period start, until the offset end when it is already past.
function periodRange(options: any, now: Date): Pick<DateFilterRange, 'start' | 'end'> {
  const unit: Unit = options.periodValue || 'day';
  const amount = Number(options.periodAmount) || 0;
  const rolling = options.periodRolling !== false;
  const offset = Number(options.offsetAmount);
  const start = rolling ? shift(now, amount, unit) : START_OF[unit](shift(now, amount - 1, unit));

  if (Number.isNaN(offset) || offset <= 0) return { start, end: null };

  const offsetUnit: Unit = options.offsetValue || 'day';
  if (rolling) {
    const end = shift(now, offset, offsetUnit);
    return { start: shift(start, offset, offsetUnit), end: end < now ? end : null };
  }

  const offsetStart = START_OF[unit](shift(now, offset, offsetUnit));
  const end = END_OF[unit](offsetStart);
  return { start: offsetStart, end: end < now ? end : null };
}

// The hours of the day kept, in minutes (null: no bound).
function hoursOf(options: any): DateFilterRange['hours'] {
  if (options?.useHoursInterval !== true) return null;
  return {
    from: options.useMin ? minutes(options.minHour, options.minMinute) : null,
    to: options.useMax ? minutes(options.maxHour, options.maxMinute) : null,
  };
}

// The dates a date filter keeps, as the API computes them (util/filterTools.js), in the viewer's time zone.
export function dateFilterRange(options: any, now: Date = new Date()): DateFilterRange {
  const bounds = options?.dateType === 'Period'
    ? periodRange(options, now)
    : { start: options?.min ? new Date(options.min) : null, end: options?.max ? new Date(options.max) : null };

  return { ...bounds, hours: hoursOf(options) };
}

// What the filter keeps of today, on a 0..24 h strip (the part before now only: nothing is measured later).
export function todaySegments(range: DateFilterRange, now: Date = new Date()): DaySegment[] {
  const dayStart = startOfDay(now).getTime();
  const percent = (time: number): number => Math.min(100, Math.max(0, (time - dayStart) / 864000));
  let from = range.start ? Math.max(range.start.getTime(), dayStart) : dayStart;
  let to = Math.min(range.end?.getTime() ?? now.getTime(), now.getTime());

  if (range.hours) {
    if (range.hours.from !== null)
      from = Math.max(from, dayStart + range.hours.from * 60000);

    if (range.hours.to !== null)
      to = Math.min(to, dayStart + range.hours.to * 60000);
  }

  return to > from ? [{ from: percent(from), to: percent(to) }] : [];
}

// Under a filter of the editor: what it keeps, in a sentence; for a date, its range and what it keeps of today.
@Component({
  selector: 'app-filter-summary',
  standalone: true,
  imports: [MatIconModule, TranslateModule],
  templateUrl: './filter-summary.component.html',
  styleUrl: './filter-summary.component.scss'
})

export class FilterSummaryComponent implements DoCheck {
  // Services
  private readonly translate: TranslateService = inject(TranslateService);

  // Variables
  // Mutated in place by the editor: its settings are compared at each check, the summary computed again
  // only when they or the minute change (stable values between two checks).
  @Input() filter: { type: string; options: any } | null | undefined;

  readonly ticks = [0, 6, 12, 18, 24];
  isDate: boolean = false;
  sentence: string = '';
  segments: DaySegment[] = [];
  nowPercent: number = 0;
  private key: string = '';

  ngDoCheck(): void {
    const now = new Date();
    now.setSeconds(0, 0);
    const key = `${now.getTime()}|${this.filter?.type}|${JSON.stringify(this.filter?.options ?? {})}`;

    if (key === this.key) return;

    this.key = key;
    this.isDate = this.filter?.type === 'date';
    const range = dateFilterRange(this.filter?.options, now);
    this.segments = this.isDate ? todaySegments(range, now) : [];
    this.nowPercent = (now.getHours() * 60 + now.getMinutes()) / 14.4;
    this.sentence = this.describe(range);
  }

  private describe(range: DateFilterRange): string {
    const options = this.filter?.options ?? {};

    switch (this.filter?.type) {
      case 'date':
        return this.describeDates(range);
      case 'string':
      case 'objectid': {
        const values = options.use === 'custom' ? [options.value].filter(Boolean) : options.values ?? [];
        return values.length ? this.t('values', { values: values.join(', ') }) : this.t('nothing');
      }
      case 'number': {
        const minSign = options.eqMin ? '≥' : '>';
        const maxSign = options.eqMax ? '≤' : '<';
        const rules = [options.useMin ? `${minSign} ${options.min}` : '', options.useMax ? `${maxSign} ${options.max}` : ''].filter(Boolean);
        return rules.length ? this.t('numbers', { rules: rules.join(` ${this.t('and')} `) }) : this.t('all');
      }
      case 'boolean':
        return this.t('values', { values: this.t(`boolean_${options.value ?? 'true'}`) });
      default:
        return '';
    }
  }

  private describeDates({ start, end, hours }: DateFilterRange): string {
    let days = this.t('all');
    if (start && end) days = this.t('between', { start: this.date(start), end: this.date(end) });
    else if (start) days = this.t('since', { start: this.date(start) });
    else if (end) days = this.t('until', { end: this.date(end) });

    if (!hours || (hours.from === null && hours.to === null))
      return days;

    const from = hours.from === null ? '00:00' : this.time(hours.from);
    const to = hours.to === null ? '24:00' : this.time(hours.to);
    return `${days}, ${this.t('hours', { from, to })}`;
  }

  private t(key: string, params?: object): string {
    return this.translate.instant(`editor.filter_summary.${key}`, params);
  }

  private date(date: Date): string {
    // The year only when it is not the current one.
    const year = date.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' as const } : {};
    return new Intl.DateTimeFormat(this.translate.currentLang || 'fr', { weekday: 'short', day: '2-digit', month: '2-digit', ...year, hour: '2-digit', minute: '2-digit' }).format(date);
  }

  private time(total: number): string {
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  }
}
